import React, { FC, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { List } from 'antd'
import { useTranslation } from 'react-i18next'
import checkMark from 'Shared/icons/check-mark-green.svg'
import { CampaignToSchedule } from 'Types/index'
import { DottedBtn } from 'Stories/DottedBtn/DottedBtn.stories'
import { PropsType } from 'Containers/ui/ParamsSelect/ListComponent/propsType'
import {
  genreAddedEventSchedulingCampaign,
  genreRemovedEventSchedulingCampaign,
  languageAddedEventSchedulingCampaign,
} from 'Helpers/tagManager'
import {
  getAmountOfCampaigns,
  getCampaignToSchedule,
} from 'Redux/selectors/campaigns'
import { getUserId } from 'Redux/selectors/auth'

import {
  AntdListItem,
  ItemsType,
  ListedItem,
  SelectItem,
} from '../ParamsSelectTypes'
import {
  GreenCheckMark,
  ItemCount,
  ItemTitle,
  ItemTitleWrapper,
  ListContainer,
  ListedItems,
  ListedItemWrapper,
  SelectedItems,
  SelectedItemsHeader,
  SelectedItemsTitle,
} from '../styles'

const ListComponent: FC<PropsType> = ({
  selectedItems,
  itemsType,
  chosenListIsOpen,
  setChosenListIsOpen,
  searchValue,
  setSelectedItems,
  listedItems,
  campaignType,
}) => {
  const { t } = useTranslation()
  const userId = useSelector(getUserId)
  const amountOfCampaigns = useSelector(getAmountOfCampaigns)
  const campaignDetails: CampaignToSchedule = useSelector(getCampaignToSchedule)

  const onSelectedItemClick = (id: number) => {
    const filteredSelectedItems = selectedItems.filter(
      item => item.value !== id
    )
    if (filteredSelectedItems.length === 0 && itemsType === 'languages') {
      setSelectedItems([{ value: 2, label: 'No vocal', selected: true }])
      return
    }
    setSelectedItems(filteredSelectedItems)
  }

  // TODO temp mapping before we have label value (after fix delete mappedSelectedItems and use selectedItems to render)
  // TODO need to refactor item names on backend (name instead genre_name and language_name)
  const mappedSelectedItems = useMemo(() => {
    if (itemsType === 'similarArtist') {
      return selectedItems.map(item => ({
        genre_name: item.label,
        selected: true,
        playlists_count: item.playlistsCount,
        id: item.value,
      }))
    }
    if (itemsType === 'genres') {
      return selectedItems.map(item => ({
        playlists_count: item.playlistsCount,
        genre_name: item.label,
        selected: true,
        id: item.value,
      }))
    }
    if (itemsType === 'languages') {
      return selectedItems.map(item => ({
        language_name: item.label,
        selected: true,
        id: item.value,
      }))
    }

    return selectedItems
  }, [selectedItems])

  const filterListedItems = ({
    listedItems,
    itemsType,
  }: {
    listedItems: ListedItem[]
    itemsType: ItemsType
  }) =>
    listedItems.filter(item => {
      const isSelected =
        selectedItems.findIndex(
          selectedItem => selectedItem.value === item.id
        ) !== -1
      return itemsType === 'languages'
        ? item?.language_name?.toLowerCase().includes(searchValue) &&
            !isSelected
        : item?.genre_name?.toLowerCase().includes(searchValue) && !isSelected
    })

  const searchedItems = useMemo(() => {
    if (itemsType === 'similarArtist') {
      return filterListedItems({ itemsType, listedItems })
    }

    if (itemsType === 'genres') {
      return filterListedItems({ itemsType, listedItems })
    }

    if (itemsType === 'languages') {
      return filterListedItems({ itemsType, listedItems })
    }

    return []
  }, [listedItems, searchValue, selectedItems])

  const setStrToUpperCase = (str: string): string =>
    str?.slice(0, 1).toUpperCase() + str?.slice(1)

  const onChosenListShow = () => {
    setChosenListIsOpen(!chosenListIsOpen)
  }

  const onItemClick = async (id: number) => {
    await new Promise(resolve => setTimeout(resolve, 500))

    const existingItemIndex = selectedItems.findIndex(item => item.value === id)
    const hasNoVocal = selectedItems.find(item => item.value === 2)

    if (existingItemIndex !== -1 && !hasNoVocal) {
      setSelectedItems(
        selectedItems.filter(
          item => item.value !== selectedItems[existingItemIndex].value
        )
      )
    } else {
      const paramItem = listedItems.find(item => item.id === id)

      let selectedItem: SelectItem

      if (itemsType === 'genres') {
        selectedItem = {
          value: paramItem && paramItem.id,
          label:
            paramItem?.genre_name && setStrToUpperCase(paramItem.genre_name),
          selected: true,
          playlistsCount: paramItem?.playlists_count,
        }

        setSelectedItems([...selectedItems, { ...selectedItem }])
      }

      if (itemsType === 'similarArtist') {
        selectedItem = {
          value: paramItem?.id,
          label: paramItem?.genre_name,
          selected: true,
          playlistsCount: paramItem?.playlists_count,
        }

        setSelectedItems([...selectedItems, { ...selectedItem }])
      }

      if (itemsType === 'languages') {
        selectedItem = {
          value: paramItem?.id,
          label:
            paramItem?.language_name &&
            setStrToUpperCase(paramItem.language_name),
          selected: true,
        }

        // If selected languages has 'No vocal' - delete it. If current item id 'No vocal' delete other selected languages
        if (paramItem && paramItem.id === 2) {
          setSelectedItems([{ ...selectedItem }])
          return
        }
        const selectedItemshasNoVocal = selectedItems.filter(
          item => item.value !== 2
        )
        setSelectedItems([...selectedItemshasNoVocal, { ...selectedItem }])
      }
    }
  }

  const mappedListedItems = useMemo(
    () =>
      searchedItems.map(item => {
        let itemUpperCaseName = ''
        const isSelected = mappedSelectedItems.some(
          selectedItem => selectedItem.id === item.id
        )

        if (itemsType === 'languages') {
          itemUpperCaseName = setStrToUpperCase(item.language_name || '')
        }

        if (itemsType === 'genres') {
          itemUpperCaseName = setStrToUpperCase(item.genre_name || '')
        }

        if (itemsType === 'languages')
          return {
            ...item,
            language_name: itemUpperCaseName,
            selected: isSelected,
          }

        if (itemsType === 'genres')
          return {
            ...item,
            genre_name: itemUpperCaseName,
            selected: isSelected,
          }

        return item
      }),
    [searchedItems, mappedSelectedItems]
  )

  const selectedItemsText = useMemo(() => {
    let itemsText: string

    switch (itemsType) {
      case 'languages':
        itemsText = `${t('createCampaignPage.chosenLanguages')} (${
          selectedItems.length
        })`
        break
      case 'genres':
        itemsText = `${t('createCampaignPage.chosenGenres')} (${
          selectedItems.length
        })`
        break
      case 'similarArtist':
        itemsText = `${t('createCampaignPage.chosenGenres')} (${
          selectedItems.length
        })`
        break
      default:
        itemsText = 'No chosen items'
    }

    return itemsText
  }, [itemsType, selectedItems])

  const ListItem: React.FunctionComponent<AntdListItem> = ({
    item,
    onClick,
  }) => {
    const [selectAnimation, setSelectAnimation] = useState<boolean>(false)
    const onClickInner = () => {
      setSelectAnimation(true)
      onClick()
    }
    const label = item.genre_name || item.language_name

    const index =
      label && label.toLowerCase().indexOf(searchValue.toLowerCase())

    const inputLength = searchValue.length

    return (
      <ListedItemWrapper onClick={onClickInner}>
        <ItemTitleWrapper>
          {(item.selected || selectAnimation) && (
            <GreenCheckMark src={checkMark} alt="green check mark" />
          )}
          {item?.selected ? (
            <ItemTitle>{label}</ItemTitle>
          ) : (
            <ItemTitle>
              {/* @ts-ignore */}
              {label?.slice(0, index)}

              <strong>
                {index === 0
                  ? searchValue.slice(0, 1).toUpperCase() + searchValue.slice(1)
                  : searchValue}
              </strong>
              {/* @ts-ignore */}
              {label?.slice(index + inputLength)}
            </ItemTitle>
          )}
        </ItemTitleWrapper>

        {itemsType !== 'languages' && (
          <ItemCount green={item.selected || selectAnimation}>
            {`${item.playlists_count} ${
              item?.playlists_count && item?.playlists_count > 1
                ? t('createCampaignPage.playlists')
                : t('createCampaignPage.playlist')
            }`}
          </ItemCount>
        )}
      </ListedItemWrapper>
    )
  }

  const renderSelectedItems = (items: Array<ListedItem>) => (
    <>
      {items.map(item => (
        <ListItem
          item={item}
          key={item.id}
          onClick={() => {
            item.id && onSelectedItemClick(item.id)

            item.hasOwnProperty('genre_name') &&
              genreRemovedEventSchedulingCampaign(
                campaignDetails.type,
                // @ts-ignore
                item.id,
                item.genre_name,
                campaignType,
                userId,
                campaignDetails.id
              )
          }}
        />
      ))}
    </>
  )

  const handlerItemClick = (item: ListedItem) => {
    item.id && onItemClick(item.id)

    item &&
      item.hasOwnProperty('language_name') &&
      languageAddedEventSchedulingCampaign(
        campaignDetails.type,
        // @ts-ignore
        item.id,
        item.language_name,
        campaignType,
        userId,
        campaignDetails.id,
        campaignDetails.type === 'first' ? 1 : amountOfCampaigns
      )

    item.hasOwnProperty('genre_name') &&
      genreAddedEventSchedulingCampaign(
        campaignDetails.type,
        // @ts-ignore
        item.id,
        item.genre_name,
        campaignType,
        userId,
        campaignDetails.id,
        campaignDetails.type === 'first' ? 1 : amountOfCampaigns
      )
  }

  const [numShow, setNumShow] = useState(50)

  const onScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    if (
      !e?.currentTarget?.offsetHeight ||
      !e?.currentTarget?.scrollTop ||
      !e?.currentTarget?.scrollHeight
    ) {
      setNumShow(mappedListedItems.length)
      return
    }

    if (
      e.currentTarget.offsetHeight + e.currentTarget.scrollTop >=
      e.currentTarget.scrollHeight - 50
    ) {
      setNumShow(prevState =>
        Math.min(prevState + 50, mappedListedItems.length)
      )
    }
  }

  return (
    <ListContainer>
      {mappedSelectedItems.length > 0 && (
        <SelectedItems>
          {itemsType !== 'languages' && (
            <SelectedItemsHeader>
              <SelectedItemsTitle>{selectedItemsText}</SelectedItemsTitle>
              <DottedBtn
                content={
                  chosenListIsOpen
                    ? t('createCampaignPage.hide')
                    : t('createCampaignPage.show')
                }
                onClick={onChosenListShow}
              />
            </SelectedItemsHeader>
          )}
          {mappedSelectedItems &&
            chosenListIsOpen &&
            itemsType !== 'languages' &&
            renderSelectedItems(mappedSelectedItems)}

          {mappedSelectedItems &&
            itemsType === 'languages' &&
            renderSelectedItems(mappedSelectedItems)}
        </SelectedItems>
      )}

      <ListedItems onScroll={onScroll}>
        <List
          dataSource={mappedListedItems.slice(0, numShow)}
          renderItem={(item: ListedItem) => (
            <ListItem item={item} onClick={() => handlerItemClick(item)} />
          )}
        />
      </ListedItems>
    </ListContainer>
  )
}

export default ListComponent
